import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { createCubeModel1 } from "../rendering/Cube/CubeModel1";
import { createCubeModel2 } from "../rendering/Cube/CubeModel2";
import { createCubeModel3 } from "../rendering/Cube/CubeModel3";
import { createCubeModel4 } from "../rendering/Cube/CubeModel4";
import { createCubeModel5 } from "../rendering/Cube/CubeModel5";
import { createPlate } from "../rendering/Plate/SpherePlate";
import { createPlate1 } from "../rendering/Plate/SpherePlate1";
import { createPlate1_1 } from "../rendering/Plate/SpherePlate1_1";
import { createPlate2 } from "../rendering/Plate/SpherePlate2";
import { createGlass } from "../rendering/Glass/Glass";
import { createGlass3 } from "../rendering/Glass/Glass3";
import{ createGlass4_1 } from "../rendering/Glass/Glass4_1";
import { createGlass5 } from "../rendering/Glass/Glass5";
import { createPlate6 } from "../rendering/Plate/SpherePlate6";
import { createGlass7 } from "../rendering/Glass/Glass7";
import { createPlate8 } from "../rendering/Plate/SpherePlate8";
import {createCube} from "../rendering/Cube/Cube";
import { createCube0 } from "../rendering/Cube/CubeModel0";
import { createGlass1_SP } from "../rendering/Glass/Glass1_SP";
import { createGlass4_SP } from "../rendering/Glass/Glass4_SP";
import { createPlate4_SP } from "../rendering/Plate/SpherePlate4_SP";

export async function loadModel(glbPath,sceneInstance, shareValue, addColor) {
  if (!glbPath) {
    console.warn("Invalid glbPath provided. Loading default cube model.");
    return createCube();
  }

  // addColor format example: "Plate-rimGeometry" -> color rim red
  function resolveAddColor(addColorStr) {
    const result = {};
    if (!addColorStr) return result;
    const parts = addColorStr.split('-');
    const target = parts[0] ? parts[0].toLowerCase() : '';
    const part = parts[1] ? parts[1].toLowerCase() : '';
    const red = 0xff0000;
    switch (target) {
      case 'plateandglass':
        // 丸皿とグラスの両方を全体ごと赤にする
        result.plateColor = red;
        result.rimColor = red;
        result.footColor = red;
        result.topColor = red;
        result.bottomColor = red;
        break;
      case 'plate':
        // 部位が未指定ならプレート全体を赤にする
        if (!part) {
          result.plateColor = red;
          result.rimColor = red;
          result.footColor = red;
          break;
        }
        switch (true) {
          case part.includes('rim'):
            result.rimColor = red;
            break;
          case part.includes('foot'):
            result.footColor = red;
            break;
          case part.includes('plate') || part.includes('main'):
            result.plateColor = red;
            break;
          default:
            break;
        }
        break;
      case 'glass':
        // 部位が未指定ならガラス全体（上端・下端）を赤にする
        if (!part) {
          result.topColor = red;
          result.bottomColor = red;
          break;
        }
        switch (true) {
          case part.includes('top'):
            result.topColor = red;
            break;
          case part.includes('bottom'):
            result.bottomColor = red;
            break;
          default:
            break;
        }
        break;
      case 'cube': {
        // 辺の組み合わせ単位の選択（例: "Cube-topPair"）。CUBE_EDGE_GROUPS のキーに変換する。
        // 旧形式の面単位（"Cube-top"等）も後方互換のため残す
        if (!part) {
          result.cubeFaces = { all: true };
        }else if (part.includes('topandside1')) {
          result.cubeEdges = 'topandSide1';
        }else if (part.includes('topandside2')) {
          result.cubeEdges = 'topandSide2';
        }else if (part.includes('topandside3')) {
          result.cubeEdges = 'topandSide3';
        }else if (part.includes('topandside4')) {
          result.cubeEdges = 'topandSide4';
        }else if (part.includes('topall')) {
          result.cubeEdges = 'topAll';
        }else if (part.includes('topleft')) {
          result.cubeEdges = 'topLeft';
        }else if (part.includes('topright')) {
          result.cubeEdges = 'topRight';
        }else if (part.includes('bottomright')) {
          result.cubeEdges = 'bottomRight';
        }else if (part.includes('bottomleft')) {
          result.cubeEdges = 'bottomLeft';
        }else {
          const faces = {};
          console.warn(`Unrecognized part "${part}" for cube. Attempting to resolve as face-based coloring.`);
          if (part.includes('top')) faces.top = true;
          else if (part.includes('left')) faces.left = true;
          else if (part.includes('right')) faces.right = true;
          result.cubeFaces = faces;
        }
        break;
      }
      default:
        break;
    }
    return result;
  }

  const addColorMap = resolveAddColor(addColor);
  // 立方体を置く x 座標（モチーフ・誤りで共通。cube_model5 の見切れを避ける値。2026-10-19に150から変更）
  const CUBE_X = 110;

  switch (glbPath) {
    case "plate": {
      const plate = createPlate(addColorMap.plateColor, addColorMap.rimColor, addColorMap.footColor);
      plate.position.set(0, 0, 0);
      return plate;
    }
    case "plate1": {
      const plate = createPlate1(undefined, addColorMap.plateColor, addColorMap.rimColor, addColorMap.footColor);
      plate.position.set(0, 0, 0);
      return plate;
    }
    case "glass": {
      const glass = createGlass(addColorMap.topColor, addColorMap.bottomColor);
      glass.position.set(0, 0, 0);
      return glass;
    }
    case "model": {
      const group = new THREE.Group();
      const glass = createGlass(addColorMap.topColor, addColorMap.bottomColor);
      const plate = createPlate(addColorMap.plateColor, addColorMap.rimColor, addColorMap.footColor);
      glass.position.set(-125, 0, 100);
      group.add(glass);
      plate.position.set(0, 0, 0);
      group.add(plate);
      return group;
    }
    case "model1": {
      const group = new THREE.Group();
      const glass = createGlass(addColorMap.topColor, addColorMap.bottomColor);
      const plate = createPlate1(shareValue, addColorMap.plateColor, addColorMap.rimColor, addColorMap.footColor);
      glass.position.set(-125, 0, 100);
      group.add(glass);
      plate.position.set(0, 0, 0);
      group.add(plate);
      return group;
    }
    case "model1_1": {
      const group = new THREE.Group();
      const glass = createGlass(addColorMap.topColor, addColorMap.bottomColor);
      const plate = createPlate1_1(shareValue, addColorMap.plateColor, addColorMap.rimColor, addColorMap.footColor);
      glass.position.set(-125, 0, 100);
      group.add(glass);
      plate.position.set(0, 0, 0);
      group.add(plate);
      return group;
    }
    case "model1_SP": {
      const group = new THREE.Group();
      const glass = createGlass1_SP(shareValue, addColorMap.topColor, addColorMap.bottomColor);
      const plate = createPlate(addColorMap.plateColor, addColorMap.rimColor, addColorMap.footColor);
      glass.position.set(-125, 0, 100);
      group.add(glass);
      plate.position.set(0, 0, 0);
      group.add(plate);
      return group;
    }
    case "model2": {
      const group = new THREE.Group();
      const glass = createGlass(addColorMap.topColor, addColorMap.bottomColor);
      const plate = createPlate2(shareValue, addColorMap.plateColor, addColorMap.rimColor, addColorMap.footColor);
      glass.position.set(-125, 0, 100);
      group.add(glass);
      plate.position.set(0, 0, 0);
      group.add(plate);
      return group;
    }
    case "model3": {
      const group = new THREE.Group();
      const glass = createGlass3(shareValue, addColorMap.topColor, addColorMap.bottomColor);
      const plate = createPlate(addColorMap.plateColor, addColorMap.rimColor, addColorMap.footColor);
      glass.position.set(-125, 0, 100);
      group.add(glass);
      plate.position.set(0, 0, 0);
      group.add(plate);
      return group;
    }
    case "model4": {
      const group = new THREE.Group();
      const glass = createGlass4_1(shareValue, addColorMap.topColor, addColorMap.bottomColor);
      const plate = createPlate(addColorMap.plateColor, addColorMap.rimColor, addColorMap.footColor);
      glass.position.set(-125, 0, 100);
      group.add(glass);
      plate.position.set(0, 0, 0);
      group.add(plate);
      return group;
    }
    case "model4_SP": {
      const group = new THREE.Group();
      const glass = createGlass4_SP(shareValue, addColorMap.topColor, addColorMap.bottomColor);
      const plate = createPlate4_SP(addColorMap.plateColor, addColorMap.rimColor, addColorMap.footColor);
      glass.position.set(-125, 0, 100);
      group.add(glass);
      plate.position.set(0, 0, 0);
      group.add(plate);
      return group;
    }
    case "model5": {
      const group = new THREE.Group();
      const glass = createGlass5(shareValue, addColorMap.topColor, addColorMap.bottomColor);
      const plate = createPlate(addColorMap.plateColor, addColorMap.rimColor, addColorMap.footColor);
      glass.position.set(-120, 0, 100); // -125 だと太くした底がふちに触れる量が約2に減り顕在化しにくいので、モチーフより少し皿へ寄せたまま残す（2026-10-19確認）
      group.add(glass);
      plate.position.set(0, 0, 0);
      group.add(plate);
      return group;
    }
    case "model6": {
      const group = new THREE.Group();
      const glass = createGlass(addColorMap.topColor, addColorMap.bottomColor);
      const plate = createPlate6(shareValue, addColorMap.plateColor, addColorMap.rimColor, addColorMap.footColor);
      glass.position.set(-125, 0, 100); // モチーフと同じ位置（2026-10-19。旧 -120 で誤り↔モチーフの切り替え時にグラスが跳んでいた）
      group.add(glass);
      plate.position.set(0, 0, 0);
      group.add(plate);
      return group;
    }
    case "model7": {
      const group = new THREE.Group();
      const glass = createGlass7(shareValue, addColorMap.topColor, addColorMap.bottomColor);
      const plate = createPlate(addColorMap.plateColor, addColorMap.rimColor, addColorMap.footColor);
      glass.position.set(-125, 0, 100);
      group.add(glass);
      plate.position.set(0, 0, 0);
      group.add(plate);
      return group;
    }
    case "model8": {
      const group = new THREE.Group();
      const glass = createGlass(addColorMap.topColor, addColorMap.bottomColor);
      const plate = createPlate8(shareValue, addColorMap.plateColor, addColorMap.rimColor, addColorMap.footColor);
      glass.position.set(-125, 0, 100); // モチーフと同じ位置（2026-10-19）
      group.add(glass);
      plate.position.set(0, 0, 0);
      group.add(plate);
      return group;
    }
    case "cube": {
      const cube= createCube(addColorMap.cubeFaces, addColorMap.cubeEdges);
      cube.position.set(CUBE_X, 60, 0);
      return cube;
    }
    case "cube_model0": {
      const cube = createCube0(addColorMap.cubeFaces, addColorMap.cubeEdges);
      cube.position.set(CUBE_X, 60, 0);
      return cube;
    }
    case "cube_model1": {
      const cube = createCubeModel1(shareValue, addColorMap.cubeFaces, addColorMap.cubeEdges);
      cube.position.set(CUBE_X, 60, 0);
      return cube;
    }
    case "cube_model2": {
      const cube = createCubeModel2(shareValue, addColorMap.cubeFaces, addColorMap.cubeEdges);
      cube.position.set(CUBE_X, 60, 0);
      return cube;
    }
    case "cube_model3": {
      const cube = createCubeModel3(shareValue, addColorMap.cubeFaces, addColorMap.cubeEdges);
      cube.position.set(CUBE_X, 60, 0);
      return cube;
    }
    case "cube_model4": {
      const cube = createCubeModel4(shareValue, addColorMap.cubeFaces, addColorMap.cubeEdges);
      cube.position.set(CUBE_X, 60, 0);
      return cube;
    }
    case "cube_model5": {
      const cube = createCubeModel5(shareValue, addColorMap.cubeFaces, addColorMap.cubeEdges);
      // 上面の回転で左上の頂点が画面左に広がり、x=150 では見切れる。そのため立方体はすべて x=110 にそろえる
      // （2026-10-19。旧＝cube_model5 だけ 110 で、誤り・中間→モチーフの切り替えで立方体が横に跳んで見えていた）
      cube.position.set(CUBE_X, 60, 0);
      return cube;
    }
    default: {
      const loader = new GLTFLoader();
      return new Promise((resolve, reject) => {
        loader.load(
          glbPath,
          (gltf) => {
            const model = gltf.scene;
            model.scale.set(400.0, 400.0, 400.0);
            model.position.set(0, -400, 0);
            resolve(model);
          },
          undefined,
          reject
        );
      });
    }
  }
}
